import type { Classroom, IslandDetails, SessionEntityType } from "@monsterverse/island-types";

import { getCurrentPlayerNameInfo, getPlayerDisplayName } from "../";
import { encodeURLString, parseURL, type URLDataType } from "../Transitions/ParseURL";
import { currentEnv, Env } from "../utils/env";
import { StudentManager } from "./StudentManager";
import { TeacherManager } from "./TeacherManager";
import { UserManager } from "./UserManager";

function getIslandDetails(classroom: Classroom): IslandDetails {
  return {
    id: `class-${classroom._id}`,
    islandName: classroom.name,
    ownerName: classroom.teacher ? getPlayerDisplayName("teacher", classroom.teacher) : "",
    ownerAvatarURL: classroom.teacher?.avatarUrl ?? "",
  };
}

export function getClassIslands(entityType: SessionEntityType) {
  const islands: IslandDetails[] = [];
  const classes =
    entityType === "teacher" ? TeacherManager.instance.sortedClasses : StudentManager.instance.sortedClasses;

  for (const classroom of classes) {
    if (!classroom.archived) {
      islands.push(getIslandDetails(classroom));
    }
  }

  return islands;
}

export function getPublicRoleplayIsland(groupId: string): IslandDetails {
  // TODO: get the correct values for the public roleplay island
  return {
    id: groupId,
    islandName: "Public Roleplay Island Test",
    ownerName: "Team YOLO",
    ownerAvatarURL: "",
  };
}

export function getPersonalOrHomeIsland(groupId: string): IslandDetails {
  const ownerAvatarURL =
    (UserManager.instance.user?.type === "student"
      ? UserManager.instance.user?.student?.avatarURL
      : UserManager.instance.user?.teacher?.avatarURL) || "";
  return {
    id: groupId,
    islandName: "",
    ownerName: getCurrentPlayerNameInfo().displayName,
    ownerAvatarURL,
  };
}

export function findIsland(groupId: string, entityType: SessionEntityType): IslandDetails | undefined {
  if (groupId.startsWith("personal-") || groupId.startsWith("community-") || groupId.startsWith("playground-")) {
    return getPersonalOrHomeIsland(groupId);
  }

  if (groupId.startsWith("public-roleplay-")) {
    return getPublicRoleplayIsland(groupId);
  }

  const islands = getClassIslands(entityType);
  for (const island of islands) {
    if (island.id === groupId) {
      return island;
    }
  }
}

export function getIslandId(island: { islandType: string; islandId: string }) {
  return `${island.islandType}-${island.islandId}`;
}

let lastOptionsLog = "";

// please update documentation if you make changes: https://www.notion.so/classdojo/Island-Query-Parameters-1895ed942e8a80f49a4ecf1f3bfad93f
// eslint-disable-next-line sonarjs/cognitive-complexity
export function getLauncherStartupOptions(): {
  entityType: SessionEntityType;
  islandId?: string;
  startupParams: string;
  URLdata?: URLDataType;
} {
  const URLdata = parseURL();
  let launchDojoIslandsAs: SessionEntityType = URLdata?.entityType === "teacher" ? "teacher" : "student";
  let islandId = URLdata?.islandId;

  if (
    islandId &&
    !islandId.startsWith("class-") &&
    !islandId.startsWith("personal-") &&
    !islandId.startsWith("home-") &&
    !islandId.startsWith("community-") &&
    !islandId.startsWith("playground-") &&
    !islandId.startsWith("public-roleplay-")
  ) {
    islandId = `class-${islandId}`;
  }

  if (currentEnv() !== Env.Prod) {
    const optionsLog = `getLauncherStartupOptions(): entityType=[${launchDojoIslandsAs}] islandId=[${islandId}]`;
    if (optionsLog !== lastOptionsLog) {
      lastOptionsLog = optionsLog;
      console.log(optionsLog);
    }
  }

  const urlSearchParams = new URLSearchParams(window.location.search);
  const startupParams = urlSearchParams.get("startupParams") || "";

  //Allow a simpler param (non encoded) for switching island ids locally
  if (currentEnv() === Env.Local && !islandId && urlSearchParams.has("islandType")) {
    switch (urlSearchParams.get("islandType") ?? "class") {
      case "class":
        islandId = "class-abcdefg";
        break;
      case "personal":
        islandId = "personal-abcdefg";
        break;
      case "community":
        islandId = "community-abcdefg";
        break;
      case "playground":
        islandId = "playground-abcdefg";
        break;
      case "publicRoleplay":
        islandId = "public-roleplay-abcdefg";
        break;
    }
  } else if (currentEnv() === Env.Dev && !islandId && urlSearchParams.has("islandType")) {
    switch (urlSearchParams.get("islandType") ?? "class") {
      case "class":
      case "personal":
        islandId = "default";
        break;
      case "community":
        islandId = "defaultCommunity";
        break;
      case "publicRoleplay":
        islandId = "defaultPublicRoleplay";
        break;
    }
  }

  // Allow full override of groupId LOCALLY for testing
  if (currentEnv() === Env.Local && !islandId && urlSearchParams.has("islandId")) {
    islandId = urlSearchParams.get("islandId") ?? undefined;
  }

  //Allow a simpler param (non encoded) for switching entity types locally
  if (currentEnv() === Env.Local && urlSearchParams.has("entityType")) {
    launchDojoIslandsAs = urlSearchParams.get("entityType") === "teacher" ? "teacher" : "student";
  }

  return {
    entityType: launchDojoIslandsAs,
    islandId,
    startupParams,
    URLdata,
  };
}

// please update documentation if you make changes: https://www.notion.so/classdojo/Island-Query-Parameters-1895ed942e8a80f49a4ecf1f3bfad93f
export function getUpdatedLauncherStartupOptions(newIslandId: string, startupParams?: Record<string, string>) {
  let URLdata = parseURL();
  if (!URLdata) {
    URLdata = { islandId: newIslandId };
  } else {
    const { entityType, spawnPoint } = URLdata;
    URLdata = { islandId: newIslandId, entityType, spawnPoint };
  }

  const urlSearchParams = new URLSearchParams(window.location.search);
  urlSearchParams.set("data", encodeURLString(JSON.stringify(URLdata)));

  if (startupParams) {
    urlSearchParams.set("startupParams", encodeURLString(JSON.stringify(startupParams)));
  }

  return urlSearchParams.toString();
}
